import { Injectable } from '@angular/core';
import { SiteSequencerService } from './site-sequencer.service';
import { ToastMessageComponent } from 'src/app/shared/components/toast-message/toast-message.component';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface PythonStaticIssue {
    severity: 'error' | 'warning';
    message: string;
    line?: number | null;
}

export interface PythonExecutionResult {
    runIndex: number;
    success: boolean;
    sectionTitle?: string;
    coverageIssues?: PythonStaticIssue[];
    error?: string;
    line?: number | null;
    stack?: string;
    output?: string;
    heapPeakBytes?: number;
    heapPeakKb?: number;
    heapPeakDeltaBytes?: number;
    heapPeakDeltaKb?: number;
    heapBaselineBytes?: number;
    heapBaselineKb?: number;
    heapAfterUserBytes?: number;
    heapAfterUserKb?: number;
    heapUserDeltaBytes?: number;
    heapUserDeltaKb?: number;
    heapFinalDeltaBytes?: number;
    heapFinalDeltaKb?: number;
    staticIssues?: PythonStaticIssue[];
}

export interface PythonExecutionBatchResult {
    success: boolean;
    runs: PythonExecutionResult[];
    staticIssues: PythonStaticIssue[];
}

@Injectable({
    providedIn: 'root'
})
export class PythonExecutorService {
    private pyodide: any;
    private initializing: Promise<void> | null = null;
    private microPython: any;
    private microPythonInitializing: Promise<void> | null = null;
    private microPythonOutputChunks: string[] = [];
    private readonly microPythonModuleUrls = [
        '/assets/micropython/micropython.mjs',
        'https://cdn.jsdelivr.net/npm/@micropython/micropython-webassembly-pyscript@0.1.4/micropython.mjs',
        'https://unpkg.com/@micropython/micropython-webassembly-pyscript@0.1.4/micropython.mjs'
    ];
    private readonly microPythonWasmUrls = [
        '/assets/micropython/micropython.wasm',
        'https://cdn.jsdelivr.net/npm/@micropython/micropython-webassembly-pyscript@0.1.4/micropython.wasm',
        'https://unpkg.com/@micropython/micropython-webassembly-pyscript@0.1.4/micropython.wasm'
    ];

    constructor(private siteSequencerService: SiteSequencerService, private toastMessage: MatSnackBar) { }

    async init(): Promise<void> {
        if (this.pyodide) return;

        if (!this.initializing) {
            this.initializing = new Promise(async (resolve) => {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/pyodide.js';

                script.onload = async () => {
                    // @ts-ignore
                    this.pyodide = await loadPyodide();
                    resolve();
                };

                document.body.appendChild(script);
            });
        }

        return this.initializing;
    }

    async executeWithStaticAnalysis(userCode: string): Promise<PythonExecutionBatchResult> {
        await this.init();

        const safeCode = this.makeCodeSafe(userCode);
        // Run static analysis on original source so we can flag raw patterns
        // (for example, while True) before safety rewriting.
        const staticIssues = await this.runStaticAnalysis(userCode);
        const hasStaticError = staticIssues.some((issue) => issue.severity === 'error');

        if (hasStaticError) {
            return {
                success: false,
                staticIssues,
                runs: [
                    {
                        runIndex: 0,
                        success: false,
                        error: 'Static analysis failed. Resolve errors before runtime execution.',
                        staticIssues
                    }
                ]
            };
        }

        const runResult = await this.runRuntimeWithMocks(safeCode);
        const coverageIssues = Array.isArray(runResult.coverageIssues) ? runResult.coverageIssues : [];
        const allIssues = [...staticIssues, ...coverageIssues];
        runResult.staticIssues = allIssues;

        return {
            success: runResult.success,
            staticIssues: allIssues,
            runs: [runResult]
        };
    }

    async executeWithMicroPythonWasm(userCode: string): Promise<PythonExecutionBatchResult> {
        await this.init();

        const safeCode = this.makeCodeSafe(userCode);
        const staticIssues = await this.runStaticAnalysis(userCode);
        const hasStaticError = staticIssues.some((issue) => issue.severity === 'error');

        if (hasStaticError) {
            return {
                success: false,
                staticIssues,
                runs: [
                    {
                        runIndex: 0,
                        success: false,
                        error: 'Static analysis failed. Resolve errors before runtime execution.',
                        staticIssues
                    }
                ]
            };
        }

        const microPythonCompatibleCode = this.prepareCodeForMicroPython(safeCode);
        const conversion = await this.convertPythonToMpyBytes(microPythonCompatibleCode);
        if (!conversion.success) {
            return {
                success: false,
                staticIssues,
                runs: [
                    {
                        runIndex: 0,
                        success: false,
                        error: conversion.error || 'MPY conversion failed.',
                        staticIssues
                    }
                ]
            };
        }

        let runResult = await this.runMicroPythonRuntimeWithMocks(microPythonCompatibleCode, conversion.mpyBytes);

        // Some runtimes fail to import compiled .mpy modules; fall back to source execution.
        if (!runResult.success && String(runResult.error || '').includes('Stage:import-mpy')) {
            runResult = await this.runMicroPythonRuntimeWithMocks(microPythonCompatibleCode);
        }
        runResult.staticIssues = staticIssues;

        return {
            success: runResult.success,
            staticIssues,
            runs: [runResult]
        };
    }


    private async runStaticAnalysis(code: string): Promise<PythonStaticIssue[]> {
        const escapedCode = this.escapeTripleQuotes(code);
        await this.pyodide.runPythonAsync(`
import ast
import json

__analysis_issues__ = []
__analysis_source__ = """${escapedCode}"""

try:
    __analysis_tree__ = ast.parse(__analysis_source__, filename="__user_code__", mode="exec")
except SyntaxError as e:
    __analysis_issues__.append({
        "severity": "error",
        "message": f"SyntaxError: {e.msg}",
        "line": getattr(e, "lineno", None)
    })
else:
    def __expr_key(expr):
        return ast.dump(expr, include_attributes=False)

    def __check_always_true_false_boolean_chains(node):
        # Detect patterns like:
        #   x != 1 or x != 2 or x != 3  -> always True
        #   x == 1 and x == 2           -> always False
        if not isinstance(node, ast.BoolOp):
            return

        if isinstance(node.op, ast.Or):
            cmp_type = ast.NotEq
            severity = "warning"
            msg = "Condition is always true due to OR-chain with multiple != checks on same variable."
        elif isinstance(node.op, ast.And):
            cmp_type = ast.Eq
            severity = "warning"
            msg = "Condition is always false due to AND-chain with multiple == checks on same variable."
        else:
            return

        left_key = None
        comparator_values = set()
        valid_chain = True

        for value in node.values:
            if not isinstance(value, ast.Compare):
                valid_chain = False
                break
            if len(value.ops) != 1 or len(value.comparators) != 1:
                valid_chain = False
                break
            if not isinstance(value.ops[0], cmp_type):
                valid_chain = False
                break

            current_left_key = __expr_key(value.left)
            if left_key is None:
                left_key = current_left_key
            elif left_key != current_left_key:
                valid_chain = False
                break

            comparator_values.add(__expr_key(value.comparators[0]))

        if valid_chain and len(comparator_values) >= 2:
            __analysis_issues__.append({
                "severity": severity,
                "message": msg,
                "line": getattr(node, "lineno", None)
            })

    def __is_call_expr(stmt, base_name, attr_name=None):
        if not isinstance(stmt, ast.Expr) or not isinstance(stmt.value, ast.Call):
            return False
        __func = stmt.value.func
        if attr_name is None:
            return isinstance(__func, ast.Name) and __func.id == base_name
        return (
            isinstance(__func, ast.Attribute) and
            isinstance(__func.value, ast.Name) and
            __func.value.id == base_name and
            __func.attr == attr_name
        )

    def __is_designed_continuous_loop(node):
        if not isinstance(node, ast.While):
            return False
        __is_true = False
        if isinstance(node.test, ast.Constant) and node.test.value is True:
            __is_true = True
        if hasattr(ast, "NameConstant") and isinstance(node.test, ast.NameConstant) and node.test.value is True:
            __is_true = True
        if not __is_true:
            return False
        if len(node.body) != 4:
            return False
        return (
            __is_call_expr(node.body[0], "connmod", "DelayMs") and
            __is_call_expr(node.body[1], "connmod", "EnterSequence") and
            __is_call_expr(node.body[2], "runContinuous") and
            __is_call_expr(node.body[3], "connmod", "ExitSequence")
        )

    def __contains_break(node):
        if not isinstance(node, ast.While):
            return False
        for __child in ast.walk(node):
            if isinstance(__child, ast.Break):
                return True
        return False

    for __node in ast.walk(__analysis_tree__):
        if isinstance(__node, ast.While):
            __is_true = False
            if isinstance(__node.test, ast.Constant) and __node.test.value is True:
                __is_true = True
            if hasattr(ast, "NameConstant") and isinstance(__node.test, ast.NameConstant) and __node.test.value is True:
                __is_true = True
            if __is_true and not __is_designed_continuous_loop(__node) and not __contains_break(__node):
                __analysis_issues__.append({
                    "severity": "warning",
                    "message": "Potential infinite loop detected: while True",
                    "line": getattr(__node, "lineno", None)
                })

        if isinstance(__node, ast.ExceptHandler) and __node.type is None:
            __analysis_issues__.append({
                "severity": "warning",
                "message": "Bare except detected. Prefer catching specific exceptions.",
                "line": getattr(__node, "lineno", None)
            })

        if isinstance(__node, ast.Call) and isinstance(__node.func, ast.Name) and __node.func.id in ("eval", "exec"):
            __analysis_issues__.append({
                "severity": "warning",
                "message": f"Use of {__node.func.id} may be unsafe.",
                "line": getattr(__node, "lineno", None)
            })

        __check_always_true_false_boolean_chains(__node)

__analysis_json__ = json.dumps(__analysis_issues__)
`);

        try {
            const raw = String(this.pyodide.globals.get('__analysis_json__') || '[]');
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private async runRuntimeWithMocks(code: string): Promise<PythonExecutionResult> {
        const escapedCode = this.escapeTripleQuotes(code);
        await this.pyodide.runPythonAsync(`
${this.getMockModules()}

import json
import sys
import traceback
import tracemalloc
from io import StringIO

__runtime_result__ = {}
__user_source__ = """${escapedCode}"""
__runtime_globals__ = {"__builtins__": __builtins__}
__executed_lines__ = set()
__coverage_issues__ = []
__branch_targets__ = []
__seen_targets__ = set()

__old_stdout__ = sys.stdout
sys.stdout = StringIO()
tracemalloc.start()
tracemalloc.clear_traces()
__heap_baseline__, _ = tracemalloc.get_traced_memory()
tracemalloc.reset_peak()

def __trace_fn__(frame, event, arg):
    if event == "line" and getattr(frame, "f_code", None) and frame.f_code.co_filename == "__user_code__":
        __executed_lines__.add(frame.f_lineno)
    return __trace_fn__

def __append_target__(kind, line, probe=None):
    if line is None:
        return
    __key = str(kind) + ":" + str(line)
    if __key in __seen_targets__:
        return
    __seen_targets__.add(__key)
    __branch_targets__.append({"kind": kind, "line": line, "probe": probe})

def __node_first_line__(node, fallback=None):
    if node is None:
        return fallback
    if hasattr(node, "lineno"):
        return getattr(node, "lineno", fallback)
    for __child in ast.walk(node):
        if hasattr(__child, "lineno"):
            return getattr(__child, "lineno", fallback)
    return fallback

def __node_source__(node):
    try:
        return ast.get_source_segment(__user_source__, node)
    except Exception:
        return None

def __run_probe__(snippet):
    if not snippet:
        return False, "Missing probe snippet"
    try:
        import textwrap
        __probe_body__ = textwrap.dedent(snippet).strip()
        if not __probe_body__:
            return False, "Empty probe snippet"
        exec(compile(__probe_body__, "__user_code__", "exec"), __runtime_globals__, __runtime_globals__)
        return True, None
    except (IndentationError, SyntaxError):
        # Probe snippets are extracted from nested control paths and may not be
        # standalone-compilable. Treat as non-actionable to avoid false warnings.
        return True, None
    except Exception as __probe_error__:
        return False, str(__probe_error__)

try:
    import ast
    __analysis_tree__ = ast.parse(__user_source__, filename="__user_code__", mode="exec")

    __parent_map__ = {}
    for __parent in ast.walk(__analysis_tree__):
        for __child in ast.iter_child_nodes(__parent):
            __parent_map__[__child] = __parent

    for __node in ast.walk(__analysis_tree__):
        if not isinstance(__node, ast.If):
            continue

        __parent_if__ = __parent_map__.get(__node)
        __is_elif__ = (
            isinstance(__parent_if__, ast.If) and
            len(getattr(__parent_if__, "orelse", [])) > 0 and
            __parent_if__.orelse[0] is __node
        )
        if __is_elif__:
            continue

        __if_stmt__ = __node.body[0] if __node.body else None
        __if_line__ = __node_first_line__(__if_stmt__, getattr(__node, "lineno", None))
        __append_target__("if", __if_line__, __node_source__(__if_stmt__))

        __current__ = __node
        while len(getattr(__current__, "orelse", [])) == 1 and isinstance(__current__.orelse[0], ast.If):
            __elif_node__ = __current__.orelse[0]
            __elif_stmt__ = __elif_node__.body[0] if __elif_node__.body else None
            __elif_line__ = __node_first_line__(__elif_stmt__, getattr(__elif_node__, "lineno", None))
            __append_target__("elif", __elif_line__, __node_source__(__elif_stmt__))
            __current__ = __elif_node__

        if getattr(__current__, "orelse", None):
            __else_stmt__ = __current__.orelse[0]
            __else_line__ = __node_first_line__(__else_stmt__, getattr(__current__, "lineno", None))
            __append_target__("else", __else_line__, __node_source__(__else_stmt__))

    for __node in ast.walk(__analysis_tree__):
        if isinstance(__node, (ast.For, getattr(ast, "AsyncFor", ast.For))):
            __for_stmt__ = __node.body[0] if __node.body else None
            __for_line__ = __node_first_line__(__for_stmt__, getattr(__node, "lineno", None))
            __append_target__("for", __for_line__, __node_source__(__for_stmt__))
            if getattr(__node, "orelse", None):
                __for_else_stmt__ = __node.orelse[0]
                __for_else_line__ = __node_first_line__(__for_else_stmt__, getattr(__node, "lineno", None))
                __append_target__("for-else", __for_else_line__, __node_source__(__for_else_stmt__))

        if isinstance(__node, ast.While):
            __while_stmt__ = __node.body[0] if __node.body else None
            __while_line__ = __node_first_line__(__while_stmt__, getattr(__node, "lineno", None))
            __append_target__("while", __while_line__, __node_source__(__while_stmt__))
            if getattr(__node, "orelse", None):
                __while_else_stmt__ = __node.orelse[0]
                __while_else_line__ = __node_first_line__(__while_else_stmt__, getattr(__node, "lineno", None))
                __append_target__("while-else", __while_else_line__, __node_source__(__while_else_stmt__))

        if isinstance(__node, ast.Try):
            __try_stmt__ = __node.body[0] if __node.body else None
            __try_line__ = __node_first_line__(__try_stmt__, getattr(__node, "lineno", None))
            __append_target__("try", __try_line__, __node_source__(__try_stmt__))
            for __handler in getattr(__node, "handlers", []):
                __except_stmt__ = __handler.body[0] if __handler.body else None
                __except_line__ = __node_first_line__(__except_stmt__, getattr(__handler, "lineno", getattr(__node, "lineno", None)))
                __append_target__("except", __except_line__, __node_source__(__except_stmt__))
            if getattr(__node, "orelse", None):
                __try_else_stmt__ = __node.orelse[0]
                __try_else_line__ = __node_first_line__(__try_else_stmt__, getattr(__node, "lineno", None))
                __append_target__("try-else", __try_else_line__, __node_source__(__try_else_stmt__))
            if getattr(__node, "finalbody", None):
                __finally_stmt__ = __node.finalbody[0]
                __finally_line__ = __node_first_line__(__finally_stmt__, getattr(__node, "lineno", None))
                __append_target__("finally", __finally_line__, __node_source__(__finally_stmt__))

    if hasattr(ast, "Match"):
        for __node in ast.walk(__analysis_tree__):
            if isinstance(__node, ast.Match):
                for __case in getattr(__node, "cases", []):
                    __case_stmt__ = __case.body[0] if getattr(__case, "body", None) else None
                    __case_line__ = __node_first_line__(__case_stmt__, getattr(__node, "lineno", None))
                    __append_target__("switch-case", __case_line__, __node_source__(__case_stmt__))

    sys.settrace(__trace_fn__)
    __compiled__ = compile(__user_source__, "__user_code__", "exec")
    exec(__compiled__, __runtime_globals__, __runtime_globals__)

    __startup__ = __runtime_globals__.get("runStartup")
    if callable(__startup__):
        __startup__()

    __continuous__ = __runtime_globals__.get("runContinuous")
    if callable(__continuous__):
        __continuous__()

    __success__ = True
    __error__ = None
    __trace__ = None
except Exception as e:
    __success__ = False
    __error__ = str(e)
    __trace__ = traceback.format_exc()
finally:
    try:
        sys.settrace(None)
    except Exception:
        pass

    for __branch in __branch_targets__:
        __branch_line__ = __branch.get("line")
        if __branch_line__ in __executed_lines__:
            continue

        __probe_ok__, __probe_err__ = __run_probe__(__branch.get("probe"))
        if __probe_ok__:
            continue

        if __probe_err__:
            __coverage_issues__.append({
                "severity": "warning",
                "message": f'Control path probe failed: {__branch.get("kind", "branch")} ({__probe_err__})',
                "line": __branch_line__
            })
        else:
            __coverage_issues__.append({
                "severity": "warning",
                "message": f'Control path not executed: {__branch.get("kind", "branch")}',
                "line": __branch_line__
            })

    __heap_current__, __heap_peak__ = tracemalloc.get_traced_memory()
    __heap_peak_delta__ = max(0, __heap_peak__ - __heap_baseline__)
    tracemalloc.stop()
    __output__ = sys.stdout.getvalue()
    sys.stdout = __old_stdout__

__line__ = None
if __trace__:
    import re
    __user_line_matches__ = re.findall(r'File "__user_code__", line (\\d+)', __trace__)
    if __user_line_matches__:
        __line__ = int(__user_line_matches__[-1])
    else:
        __line_matches__ = re.findall(r'line (\\d+)', __trace__)
        if __line_matches__:
            __line__ = int(__line_matches__[-1])

__runtime_result__ = {
    "runIndex": 0,
    "success": __success__,
    "error": __error__,
    "line": __line__,
    "stack": __trace__,
    "output": __output__,
    "coverageIssues": __coverage_issues__,
    "heapPeakBytes": __heap_peak__,
    "heapPeakKb": round(__heap_peak__ / 1024, 2),
    "heapPeakDeltaBytes": __heap_peak_delta__,
    "heapPeakDeltaKb": round(__heap_peak_delta__ / 1024, 2),
}

__runtime_json__ = json.dumps(__runtime_result__)
`);

        try {
            const raw = String(this.pyodide.globals.get('__runtime_json__') || '{}');
            const parsed = JSON.parse(raw);
            if (parsed?.stack) {
                parsed.stack = this.sanitizeTraceback(String(parsed.stack));
            }
            return parsed;
        } catch (error: any) {
            return {
                runIndex: 0,
                success: false,
                error: error?.message || 'Runtime execution failed to parse.'
            };
        }
    }

    private async initMicroPython(): Promise<void> {
        if (this.microPython) return;

        if (!this.microPythonInitializing) {
            this.microPythonInitializing = (async () => {
                let loadMicroPython: any = null;
                const moduleErrors: string[] = [];

                for (const moduleUrl of this.microPythonModuleUrls) {
                    try {
                        const moduleObj: any = await import(/* webpackIgnore: true */ moduleUrl);
                        if (typeof moduleObj?.loadMicroPython === 'function') {
                            loadMicroPython = moduleObj.loadMicroPython;
                            break;
                        }
                        moduleErrors.push(`${moduleUrl} (missing loadMicroPython export)`);
                    } catch (error: any) {
                        moduleErrors.push(`${moduleUrl} (${error?.message || 'load failed'})`);
                    }
                }

                if (!loadMicroPython) {
                    throw new Error(`Unable to load MicroPython module. Tried: ${moduleErrors.join(' | ')}`);
                }

                const wasmErrors: string[] = [];
                for (const wasmUrl of this.microPythonWasmUrls) {
                    try {
                        this.microPython = await loadMicroPython({
                            linebuffer: true,
                            url: wasmUrl,
                            stdout: (message: any) => this.microPythonOutputChunks.push(this.normalizeMicroPythonOutputChunk(message)),
                            stderr: (message: any) => this.microPythonOutputChunks.push(this.normalizeMicroPythonOutputChunk(message))
                        });
                        return;
                    } catch (error: any) {
                        wasmErrors.push(`${wasmUrl} (${error?.message || 'init failed'})`);
                    }
                }

                throw new Error(`Unable to initialize MicroPython wasm. Tried: ${wasmErrors.join(' | ')}`);
            })();
        }

        try {
            return await this.microPythonInitializing;
        } catch (error) {
            this.microPythonInitializing = null;
            throw error;
        }
    }

    private async runMicroPythonRuntimeWithMocks(code: string, mpyBytes?: Uint8Array): Promise<PythonExecutionResult> {
        try {
            await this.initMicroPython();
        } catch (error: any) {
            return {
                runIndex: 0,
                success: false,
                error: `MicroPython runtime init failed: ${error?.message || 'Unknown error'}`
            };
        }

        const escapedCode = this.escapeTripleQuotes(code);
        const useCompiledMpy = !!(mpyBytes && mpyBytes.length > 0);
        this.microPythonOutputChunks = [];

        if (useCompiledMpy) {
            try {
                this.writeMicroPythonFile('__user_code__.mpy', mpyBytes as Uint8Array);
            } catch (error: any) {
                return {
                    runIndex: 0,
                    success: false,
                    error: `Unable to write compiled .mpy into MicroPython FS: ${error?.message || 'Unknown error'}`
                };
            }
        }

        try {
            await this.executeMicroPythonCode(this.getMockModules());
        } catch (error: any) {
            return {
                runIndex: 0,
                success: false,
                error: `Mock bootstrap failed: ${error?.message || error?.toString?.() || 'Unknown error'}`
            };
        }

        try {
            await this.executeMicroPythonCode(`
import sys
try:
    import gc as _gc
except ImportError:
    _gc = None
try:
    import uio as _io
except ImportError:
    _io = None

__success__ = False
__error__ = None
__trace__ = ""
__line__ = None
__heap_peak__ = 0
__heap_peak_delta__ = 0
__heap_baseline__ = 0
__heap_after_user__ = 0
__heap_user_delta__ = 0
__heap_final_delta__ = 0
__output__ = ""
__heap_before__ = 0
__heap_after__ = 0
__out_buf__ = None
__old_stdout__ = None
__use_mpy__ = ${useCompiledMpy ? 'True' : 'False'}
__stage__ = "user-entry"
__MP_RESULT_LINE__ = "__MP_RESULT__|success=False|heap_peak=0|heap_peak_delta=0|error=Stage:user-entry"
__MP_OUTPUT_LINE__ = ""
__MP_TRACE_LINE__ = ""

try:
    if _io is not None:
        try:
            __out_buf__ = _io.StringIO()
        except Exception:
            __out_buf__ = None

    if __out_buf__ is not None and hasattr(sys, "stdout"):
        try:
            __old_stdout__ = sys.stdout
            sys.stdout = __out_buf__
        except Exception:
            __old_stdout__ = None

    if _gc is not None:
        try:
            _gc.collect()
        except Exception:
            pass

    if _gc is not None and hasattr(_gc, "mem_alloc"):
        try:
            __heap_before__ = _gc.mem_alloc()
        except Exception:
            __heap_before__ = 0
    __heap_baseline__ = __heap_before__

    try:
        __stage__ = "user-entry"
        if __use_mpy__:
            __stage__ = "import-mpy"
            __module_name__ = "__user_code__"
            if hasattr(sys, "path"):
                try:
                    if "" not in sys.path:
                        sys.path.append("")
                except Exception:
                    pass

            if __module_name__ in sys.modules:
                try:
                    del sys.modules[__module_name__]
                except Exception:
                    pass

            __module_obj__ = __import__(__module_name__)
            __startup__ = getattr(__module_obj__, "runStartup", None)
            if callable(__startup__):
                __stage__ = "runStartup"
                __startup__()

            __continuous__ = getattr(__module_obj__, "runContinuous", None)
            if callable(__continuous__):
                __stage__ = "runContinuous"
                __continuous__()
        else:
            __stage__ = "compile-source"
            __globals__ = {"__builtins__": __builtins__}
            __compiled__ = compile("""${escapedCode}""", "__user_code__", "exec")
            exec(__compiled__, __globals__, __globals__)

            __startup__ = __globals__.get("runStartup")
            if callable(__startup__):
                __stage__ = "runStartup"
                __startup__()

            __continuous__ = __globals__.get("runContinuous")
            if callable(__continuous__):
                __stage__ = "runContinuous"
                __continuous__()

        if _gc is not None and hasattr(_gc, "mem_alloc"):
            try:
                __heap_after_user__ = _gc.mem_alloc()
            except Exception:
                __heap_after_user__ = __heap_baseline__
        else:
            __heap_after_user__ = __heap_baseline__

        __success__ = True
        __stage__ = "completed"
    except Exception as e:
        __success__ = False
        __error__ = str(e)
        if _gc is not None and hasattr(_gc, "mem_alloc"):
            try:
                __heap_after_user__ = _gc.mem_alloc()
            except Exception:
                __heap_after_user__ = __heap_baseline__
        else:
            __heap_after_user__ = __heap_baseline__
        try:
            if _io is not None:
                __tb_buf__ = _io.StringIO()
                sys.print_exception(e, __tb_buf__)
                __trace__ = __tb_buf__.getvalue()
            else:
                __trace__ = repr(e)
        except Exception:
            __trace__ = repr(e)
except Exception as e:
    __success__ = False
    __error__ = str(e)
    if _gc is not None and hasattr(_gc, "mem_alloc"):
        try:
            __heap_after_user__ = _gc.mem_alloc()
        except Exception:
            __heap_after_user__ = __heap_baseline__
    else:
        __heap_after_user__ = __heap_baseline__
    try:
        if _io is not None:
            __tb_buf__ = _io.StringIO()
            sys.print_exception(e, __tb_buf__)
            __trace__ = __tb_buf__.getvalue()
        else:
            __trace__ = repr(e)
    except Exception:
        __trace__ = repr(e)
finally:
    if _gc is not None:
        try:
            _gc.collect()
        except Exception:
            pass

    if _gc is not None and hasattr(_gc, "mem_alloc"):
        try:
            __heap_after__ = _gc.mem_alloc()
        except Exception:
            __heap_after__ = __heap_before__
    else:
        __heap_after__ = __heap_before__

    __heap_peak__ = max(__heap_baseline__, __heap_after_user__, __heap_after__)
    __heap_peak_delta__ = max(0, __heap_peak__ - __heap_baseline__)
    __heap_user_delta__ = max(0, __heap_after_user__ - __heap_baseline__)
    __heap_final_delta__ = max(0, __heap_after__ - __heap_baseline__)

    if __out_buf__ is not None:
        try:
            __output__ = __out_buf__.getvalue()
        except Exception:
            __output__ = ""

    if __old_stdout__ is not None and hasattr(sys, "stdout"):
        try:
            sys.stdout = __old_stdout__
        except Exception:
            pass

__error_text__ = ("Stage:" + __stage__ + " | " + str(__error__)) if __error__ is not None else ("Stage:" + __stage__ + " | None")
__MP_RESULT_LINE__ = "__MP_RESULT__|success=" + str(__success__) + "|heap_peak=" + str(__heap_peak__) + "|heap_peak_delta=" + str(__heap_peak_delta__) + "|heap_baseline=" + str(__heap_baseline__) + "|heap_after_user=" + str(__heap_after_user__) + "|heap_user_delta=" + str(__heap_user_delta__) + "|heap_final_delta=" + str(__heap_final_delta__) + "|error=" + __error_text__
__MP_OUTPUT_LINE__ = "__MP_OUTPUT__|" + __output__.replace("\\n", "\\\\n") if __output__ else ""
__MP_TRACE_LINE__ = "__MP_TRACE__|" + __trace__.replace("\\n", "\\\\n") if __trace__ else ""

try:
    print(__MP_RESULT_LINE__)
except Exception:
    pass
if __MP_OUTPUT_LINE__:
    try:
        print(__MP_OUTPUT_LINE__)
    except Exception:
        pass
if __MP_TRACE_LINE__:
    try:
        print(__MP_TRACE_LINE__)
    except Exception:
        pass
`);
        } catch (error: any) {
            const runtimeOutput = this.microPythonOutputChunks.join('').trim();
            const errorMessage =
                error?.message ||
                error?.toString?.() ||
                'MicroPython runtime execution failed';
            return {
                runIndex: 0,
                success: false,
                error: runtimeOutput ? `${errorMessage}\n${runtimeOutput}` : errorMessage,
                stack: error?.stack
            };
        }

        await this.waitForMicroPythonResultUpdate('__MP_RESULT__|success=False|heap_peak=0|heap_peak_delta=0|error=Stage:user-entry');

        const rawOutput = this.microPythonOutputChunks.join('');
        const parsedFromOutput = this.parseMicroPythonOutput(rawOutput);
        if (!parsedFromOutput.success && parsedFromOutput.error === 'Unable to parse MicroPython runtime result.') {
            const parsedFromGlobals = this.parseMicroPythonResultFromGlobals();
            if (parsedFromGlobals) {
                return parsedFromGlobals;
            }

            const globalsDebug = this.getMicroPythonGlobalsDebug();
            return {
                runIndex: 0,
                success: false,
                error: `Unable to parse MicroPython runtime result. ${globalsDebug}`,
                output: rawOutput || undefined
            };
        }

        return parsedFromOutput;
    }

    private parseMicroPythonOutput(rawOutput: string): PythonExecutionResult {
        const normalized = String(rawOutput || '').replace(/\r\n/g, '\n');
        const metricLine = this.getLastMarkerLine(normalized, '__MP_RESULT__|');
        const outputLine = this.getLastMarkerLine(normalized, '__MP_OUTPUT__|');
        const traceLine = this.getLastMarkerLine(normalized, '__MP_TRACE__|');

        if (!metricLine) {
            return {
                runIndex: 0,
                success: false,
                error: 'Unable to parse MicroPython runtime result.',
                output: normalized || undefined
            };
        }

        const tokenMap = metricLine
            .split('|')
            .slice(1)
            .reduce((acc: Record<string, string>, token: string) => {
                const idx = token.indexOf('=');
                if (idx <= 0) return acc;
                acc[token.slice(0, idx)] = token.slice(idx + 1);
                return acc;
            }, {});

        const successToken = tokenMap['success'];
        const success = successToken === 'True' || successToken === 'true' || successToken === '1';
        const heapPeakBytes = Number(tokenMap['heap_peak'] || 0);
        const heapPeakDeltaBytes = Number(tokenMap['heap_peak_delta'] || 0);
        const heapBaselineBytes = Number(tokenMap['heap_baseline'] || 0);
        const heapAfterUserBytes = Number(tokenMap['heap_after_user'] || 0);
        const heapUserDeltaBytes = Number(tokenMap['heap_user_delta'] || 0);
        const heapFinalDeltaBytes = Number(tokenMap['heap_final_delta'] || 0);
        const errorVal = tokenMap['error'];
        const traceRaw = traceLine ? traceLine.replace('__MP_TRACE__|', '').replace(/\\n/g, '\n') : '';
        const trace = this.sanitizeTraceback(traceRaw);
        const outputFromMarker = outputLine ? outputLine.replace('__MP_OUTPUT__|', '').replace(/\\n/g, '\n') : '';
        const outputFallback = outputFromMarker ? '' : this.extractNonMarkerOutput(normalized);
        const output = outputFromMarker || outputFallback;
        const extractedLine = this.extractUserCodeLineFromTrace(traceRaw);
        const line = this.adjustToOriginalUserCodeLine(extractedLine, this.getMicroPythonCompatPreludeLineOffset());

        return {
            runIndex: 0,
            success,
            error: (!success && errorVal && errorVal !== 'None') ? errorVal : undefined,
            line,
            stack: trace || undefined,
            output: output || undefined,
            heapPeakBytes: Number.isFinite(heapPeakBytes) ? heapPeakBytes : 0,
            heapPeakKb: Number.isFinite(heapPeakBytes) ? Number((heapPeakBytes / 1024).toFixed(2)) : 0,
            heapPeakDeltaBytes: Number.isFinite(heapPeakDeltaBytes) ? heapPeakDeltaBytes : 0,
            heapPeakDeltaKb: Number.isFinite(heapPeakDeltaBytes) ? Number((heapPeakDeltaBytes / 1024).toFixed(2)) : 0,
            heapBaselineBytes: Number.isFinite(heapBaselineBytes) ? heapBaselineBytes : 0,
            heapBaselineKb: Number.isFinite(heapBaselineBytes) ? Number((heapBaselineBytes / 1024).toFixed(2)) : 0,
            heapAfterUserBytes: Number.isFinite(heapAfterUserBytes) ? heapAfterUserBytes : 0,
            heapAfterUserKb: Number.isFinite(heapAfterUserBytes) ? Number((heapAfterUserBytes / 1024).toFixed(2)) : 0,
            heapUserDeltaBytes: Number.isFinite(heapUserDeltaBytes) ? heapUserDeltaBytes : 0,
            heapUserDeltaKb: Number.isFinite(heapUserDeltaBytes) ? Number((heapUserDeltaBytes / 1024).toFixed(2)) : 0,
            heapFinalDeltaBytes: Number.isFinite(heapFinalDeltaBytes) ? heapFinalDeltaBytes : 0,
            heapFinalDeltaKb: Number.isFinite(heapFinalDeltaBytes) ? Number((heapFinalDeltaBytes / 1024).toFixed(2)) : 0
        };
    }

    private extractNonMarkerOutput(text: string): string {
        return text
            .split('\n')
            .map((line) => line.trim())
            .filter((line) =>
                line.length > 0 &&
                !line.startsWith('__MP_RESULT__|') &&
                !line.startsWith('__MP_OUTPUT__|') &&
                !line.startsWith('__MP_TRACE__|')
            )
            .join('\n');
    }

    private getLastMarkerLine(text: string, markerPrefix: string): string {
        const escaped = markerPrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(?:^|\\n)\\s*(${escaped}[^\\n\\r]*)`, 'g');
        let match: RegExpExecArray | null = null;
        let last = '';
        // eslint-disable-next-line no-cond-assign
        while ((match = regex.exec(text)) !== null) {
            last = match[1];
        }
        return last;
    }

    private parseMicroPythonResultFromGlobals(): PythonExecutionResult | null {
        try {
            const globals = this.microPython?.globals;
            if (!globals) {
                return null;
            }

            const dictObj: any = (globals as any).__dict__;
            const getFromDict = (key: string): any => {
                try {
                    if (dictObj && typeof dictObj.get === 'function') {
                        return dictObj.get(key);
                    }
                } catch { }
                try {
                    if (dictObj) {
                        return dictObj[key];
                    }
                } catch { }
                return undefined;
            };
            const getFromGlobals = (key: string): any => {
                try {
                    if (typeof globals.get === 'function') {
                        return globals.get(key);
                    }
                } catch { }
                try {
                    return (globals as any)[key];
                } catch { }
                return undefined;
            };

            const metricLine = String(getFromGlobals('__MP_RESULT_LINE__') ?? getFromDict('__MP_RESULT_LINE__') ?? '');
            if (!metricLine) {
                return null;
            }

            const outputLine = String(getFromGlobals('__MP_OUTPUT_LINE__') ?? getFromDict('__MP_OUTPUT_LINE__') ?? '');
            const traceLine = String(getFromGlobals('__MP_TRACE_LINE__') ?? getFromDict('__MP_TRACE_LINE__') ?? '');
            return this.parseMicroPythonOutput([metricLine, outputLine, traceLine].filter(Boolean).join('\n'));
        } catch {
            return null;
        }
    }

    private getMicroPythonGlobalsDebug(): string {
        try {
            const globals = this.microPython?.globals;
            if (!globals) {
                return 'Globals unavailable.';
            }
            const dictObj: any = (globals as any).__dict__;
            const metricFromGet = (() => {
                try {
                    return typeof globals.get === 'function' ? globals.get('__MP_RESULT_LINE__') : undefined;
                } catch {
                    return '[get error]';
                }
            })();
            const metricFromDict = (() => {
                try {
                    if (dictObj && typeof dictObj.get === 'function') {
                        return dictObj.get('__MP_RESULT_LINE__');
                    }
                    return dictObj ? dictObj['__MP_RESULT_LINE__'] : undefined;
                } catch {
                    return '[dict error]';
                }
            })();
            return `globals(__MP_RESULT_LINE__) get=${String(metricFromGet)} dict=${String(metricFromDict)}`;
        } catch {
            return 'Globals debug unavailable.';
        }
    }

    private async waitForMicroPythonResultUpdate(initialMarker: string, timeoutMs: number = 2500): Promise<void> {
        const start = Date.now();
        while ((Date.now() - start) < timeoutMs) {
            const marker = this.readMicroPythonGlobalString('__MP_RESULT_LINE__');
            if (marker && marker !== initialMarker) {
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, 25));
        }
    }

    private readMicroPythonGlobalString(key: string): string {
        try {
            const globals = this.microPython?.globals;
            if (!globals) return '';
            const dictObj: any = (globals as any).__dict__;
            let value: any = undefined;
            try {
                if (typeof globals.get === 'function') {
                    value = globals.get(key);
                }
            } catch { }
            if (value === undefined && dictObj) {
                try {
                    if (typeof dictObj.get === 'function') {
                        value = dictObj.get(key);
                    } else {
                        value = dictObj[key];
                    }
                } catch { }
            }
            return value == null ? '' : String(value);
        } catch {
            return '';
        }
    }

    private escapeTripleQuotes(code: string): string {
        return code.replace(/\\/g, '\\\\').replace(/"""/g, '\\"\\"\\"');
    }

    private async convertPythonToMpyBytes(code: string): Promise<{ success: boolean; mpyBytes?: Uint8Array; error?: string }> {
        return await new Promise((resolve) => {
            this.siteSequencerService.getMicroPythonConversion(code, '__user_code__').subscribe({
                next: async (res: any) => {
                    try {
                        const conversionBlob = res instanceof Blob ? res : new Blob([res]);
                        const mpyBytes = await this.blobToBytes(conversionBlob);
                        if (!mpyBytes || mpyBytes.length === 0) {
                            resolve({
                                success: false,
                                error: 'MPY conversion failed: Empty .mpy payload.'
                            });
                            return;
                        }
                        resolve({ success: true, mpyBytes });
                    } catch (error: any) {
                        resolve({
                            success: false,
                            error: `MPY conversion failed: ${error?.message || 'Invalid blob response'}`
                        });
                    }
                },
                error: (error: any) => {
                    resolve({
                        success: false,
                        error: `MPY conversion failed: ${error?.message || 'Unknown error'}`
                    });
                    this.toastMessage.openFromComponent(ToastMessageComponent, {
                          duration: 5000,
                          data: {
                            type: 'error',
                            message: `Something went wrong in downloading the sequence code, please try again.`
                          },
                        });
                }
            });
        });
    }

    private async blobToBytes(blob: Blob): Promise<Uint8Array> {
        return new Uint8Array(await blob.arrayBuffer());
    }

    private writeMicroPythonFile(path: string, data: Uint8Array): void {
        const fs = this.microPython?.FS;
        if (!fs || typeof fs.writeFile !== 'function') {
            throw new Error('MicroPython FS.writeFile is unavailable.');
        }
        fs.writeFile(path, data);
    }

    private normalizeMicroPythonOutputChunk(chunk: any): string {
        if (typeof chunk === 'string') {
            return chunk;
        }

        if (chunk instanceof Uint8Array) {
            try {
                return new TextDecoder().decode(chunk);
            } catch {
                return Array.from(chunk).map((b) => String.fromCharCode(b)).join('');
            }
        }

        if (Array.isArray(chunk)) {
            try {
                return new TextDecoder().decode(new Uint8Array(chunk));
            } catch {
                return chunk.map((b) => String.fromCharCode(Number(b) || 0)).join('');
            }
        }

        return String(chunk ?? '');
    }

    private makeCodeSafe(code: string): string {
        return code.replace(/while\s+(True|1)\s*:/g, 'for __loop_guard in range(10):');
    }

    private async executeMicroPythonCode(code: string): Promise<void> {
        const runtime = this.microPython as any;
        if (!runtime) {
            throw new Error('MicroPython runtime is not initialized.');
        }

        if (typeof runtime.runPythonAsync === 'function') {
            try {
                await runtime.runPythonAsync(code);
            } catch (error: any) {
                // Some micropython.mjs builds throw "NULL object" while converting
                // return value even though code execution/output has completed.
                if (!String(error?.message || '').includes('NULL object')) {
                    throw error;
                }
            }
            return;
        }

        // Fallback for runtimes that only expose sync API.
        if (typeof runtime.runPython === 'function') {
            try {
                runtime.runPython(code);
            } catch (error: any) {
                if (!String(error?.message || '').includes('NULL object')) {
                    throw error;
                }
            }
            return;
        }

        throw new Error('MicroPython runtime does not expose runPython/runPythonAsync.');
    }

    private prepareCodeForMicroPython(code: string): string {
        const prelude = this.getMicroPythonCompatPrelude().replace(/\s+$/, '');
        return `${prelude}\n${code}`;
    }

    private extractUserCodeLineFromTrace(trace: string): number | null {
        if (!trace) {
            return null;
        }

        const userCodeRegex = /File "__user_code__", line (\d+)/g;
        let match: RegExpExecArray | null = null;
        let lastUserCodeLine: number | null = null;
        // eslint-disable-next-line no-cond-assign
        while ((match = userCodeRegex.exec(trace)) !== null) {
            lastUserCodeLine = parseInt(match[1], 10);
        }

        if (lastUserCodeLine !== null && Number.isFinite(lastUserCodeLine)) {
            return lastUserCodeLine;
        }

        const anyLineRegex = /line (\d+)/g;
        let lastAnyLine: number | null = null;
        // eslint-disable-next-line no-cond-assign
        while ((match = anyLineRegex.exec(trace)) !== null) {
            lastAnyLine = parseInt(match[1], 10);
        }
        return (lastAnyLine !== null && Number.isFinite(lastAnyLine)) ? lastAnyLine : null;
    }

    private sanitizeTraceback(trace: string): string {
        const lines = String(trace || '').replace(/\r\n/g, '\n').split('\n');
        const sanitized: string[] = [];
        let skipIndentedContext = false;

        for (const line of lines) {
            const frameMatch = line.match(/^\s*File "([^"]+)", line \d+, in .*$/);
            if (frameMatch) {
                const fileName = frameMatch[1];
                const keepFrame = fileName === '__user_code__';
                skipIndentedContext = !keepFrame;
                if (keepFrame) {
                    sanitized.push(line);
                }
                continue;
            }

            if (skipIndentedContext && /^\s+/.test(line) && !/^\s*File "([^"]+)", line \d+, in .*$/.test(line)) {
                continue;
            }

            skipIndentedContext = false;
            sanitized.push(line);
        }

        return sanitized.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    }

    private adjustToOriginalUserCodeLine(line: number | null, offset: number = 0): number | null {
        if (line === null || !Number.isFinite(line)) {
            return null;
        }
        const adjusted = line - offset;
        return adjusted > 0 ? adjusted : 1;
    }

    private getMicroPythonCompatPreludeLineOffset(): number {
        const prelude = this.getMicroPythonCompatPrelude().replace(/\s+$/, '');
        return prelude.length ? prelude.split('\n').length : 0;
    }

    private getMicroPythonCompatPrelude(): string {
        return `# Compatibility shim for CPython modules missing in MicroPython.
import sys
try:
    import types
except ImportError:
    class _CompatModule:
        def __init__(self, name):
            self.__name__ = name

    class _CompatTypes:
        @staticmethod
        def ModuleType(name):
            return _CompatModule(name)

    types = _CompatTypes()
    sys.modules["types"] = types
`;
    }

    private getMockModules(): string {
        return `
import sys
try:
    import types
except ImportError:
    class _CompatModule:
        def __init__(self, name):
            self.__name__ = name

    class _CompatTypes:
        @staticmethod
        def ModuleType(name):
            return _CompatModule(name)

    types = _CompatTypes()

connmod = types.ModuleType("connmod")
_registers = {}
_relays = [0]*8
_analogs = [0]*4
_universal_inputs = [0]*8
_universal_modes = [0]*8
_pid_state = {}

def SetRelayOutput(i,v): _relays[i]=v
def ReadRelayOutputMappedValHoldingReg(i): return _relays[i]
def SetAnalogOutput(i,v): _analogs[i]=v
def ReadAnalogOutputMappedValHoldingReg(i): return _analogs[i]
def GetUniversalInputValue(i): return _universal_inputs[i]
def WriteUniversalInputMappedValInputReg(i,v): _universal_inputs[i]=v
def SetUniversalInputMode(i,m): _universal_modes[i]=m
def ReadUniversalInputMappingConfigHoldingReg(i): return _universal_modes[i]
def WriteDataTypeRegister(d,r,a,v): _registers[(d,r,a)] = v
def ReadDataTypeRegister(d,r,a): return _registers.get((d,r,a),0)
def DelayMs(ms): pass
def EnterSequence(): pass
def ExitSequence(): pass
def ConfigureHomeScreenInput(*a): pass
def UpdateHomeScreenInput(*a): pass
def ConfigureCustomDatatypeModbusRegister(*a): pass

def PIDControllerProcess(i,pv,sp,kp,ki,kd,minv,maxv,scale):
    if i not in _pid_state:
        _pid_state[i] = {"integral":0,"prev":0}
    error = sp - pv
    _pid_state[i]["integral"] += error
    derivative = error - _pid_state[i]["prev"]
    _pid_state[i]["prev"] = error
    output = kp*error + ki*_pid_state[i]["integral"] + kd*derivative
    return max(min(output,maxv),minv)

def __dump_state__():
    return {"registers": _registers, "relays": _relays, "analogs": _analogs, "inputs": _universal_inputs}

for name in list(locals().keys()):
    if callable(locals()[name]) and name not in ["types","sys"]:
        setattr(connmod,name,locals()[name])

def _missing(name):
    def _fn(*a,**k):
        if name.startswith("Read") or name.startswith("Get"):
            return 0
        return None
    return _fn

try:
    setattr(connmod, "__getattr__", _missing)
except Exception:
    # Some MicroPython builds may not allow module-level __getattr__.
    pass
sys.modules["connmod"] = connmod

uniInputMapping = types.ModuleType("uniInputMapping")
def ConvertValueFromIndex(v,i): return v
def ScaleValue(inputValue,inputMin,inputMax,outputMin,outputMax):
    try:
        denom = (inputMax - inputMin)
        if denom == 0:
            return outputMin
        return ((inputValue - inputMin) * (outputMax - outputMin) / denom) + outputMin
    except Exception:
        return outputMin
def GetSensorModeFromIndex(i): return i
def GetSensorNameFromIndex(i): return "Sensor_" + str(i)
def GetSensorUnitFromIndex(i): return "Unit"
uniInputMapping.ConvertValueFromIndex = ConvertValueFromIndex
uniInputMapping.ScaleValue = ScaleValue
uniInputMapping.GetSensorModeFromIndex = GetSensorModeFromIndex
uniInputMapping.GetSensorNameFromIndex = GetSensorNameFromIndex
uniInputMapping.GetSensorUnitFromIndex = GetSensorUnitFromIndex
sys.modules["uniInputMapping"] = uniInputMapping
`;
    }
}
